/**
 * Derived explorer data: filtered + sorted row order, display columns and header
 * summaries. Everything is recomputed from the sample in memory — explorer
 * interactions never re-request the preview (SC-007).
 */

import { useEffect, useMemo, useState } from 'react';
import { applyFilters } from '../model/filter';
import { sortIndices } from '../model/sort';
import { summarizeAll } from '../model/profile';
import { resolveDisplayColumns } from '../model/layout';
import type { ColumnLayout, Filter, Row, Schema, SortState } from '../model/types';

/** Debounce so typing in quick search doesn't re-filter on every keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useExplorerData(args: {
  rows: Row[];
  schema: Schema;
  layout: ColumnLayout;
  filters: Filter[];
  quickSearch: string;
  sort: SortState;
}) {
  const { rows, schema, layout, filters, quickSearch, sort } = args;
  const search = useDebounced(quickSearch, 150);

  const filtered = useMemo(
    () => applyFilters(rows, schema, { filters, quickSearch: search }),
    [rows, schema, filters, search],
  );
  const indices = useMemo(() => sortIndices(rows, filtered, sort, schema), [rows, filtered, sort, schema]);

  const columns = useMemo(() => resolveDisplayColumns(schema, layout), [schema, layout]);

  // Profiles follow the filtered set (FR-019); sort order doesn't affect them.
  const summaries = useMemo(() => summarizeAll(rows, filtered, schema.columns), [rows, filtered, schema]);

  const filteredColumns = useMemo(() => new Set(filters.map((f) => f.column)), [filters]);

  return { indices, columns, summaries, filteredColumns };
}
